import { useEffect } from 'react';

export interface SEOConfig {
  title: string;
  description: string;
  keywords: string[];
  canonical?: string;
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  lang?: 'en' | 'fr' | 'es';
  alternates?: {
    lang: 'en' | 'fr' | 'es';
    url: string;
  }[];
}

export interface MultiLangSEO {
  en: SEOConfig;
  fr: SEOConfig;
  es: SEOConfig;
}

// Default SEO configurations for different languages
export const defaultSEO: MultiLangSEO = {
  en: {
    title: 'PDF Form Editor - Create Interactive PDF Forms Online',
    description: 'Create interactive PDF forms online with our free PDF editor. Add text fields, checkboxes, and form elements to any PDF document. Fast, secure, and browser-based.',
    keywords: [
      'pdf editor',
      'pdf form creator',
      'interactive pdf',
      'pdf form fields',
      'online pdf editor',
      'pdf text fields',
      'pdf checkboxes',
      'form pdf',
      'pdf fillable forms',
      'pdf form builder'
    ],
    canonical: 'https://form-pdf.com/',
    ogImage: 'https://form-pdf.com/og-image.jpg',
    twitterCard: 'summary_large_image',
    lang: 'en'
  },
  fr: {
    title: 'Éditeur de Formulaires PDF - Créez des Formulaires PDF Interactifs en Ligne',
    description: 'Créez des formulaires PDF interactifs en ligne avec notre éditeur PDF gratuit. Ajoutez des champs de texte, des cases à cocher et des éléments de formulaire à tout document PDF. Rapide, sécurisé et basé sur navigateur.',
    keywords: [
      'éditeur pdf',
      'créateur de formulaires pdf',
      'pdf interactif',
      'champs de formulaire pdf',
      'éditeur pdf en ligne',
      'champs de texte pdf',
      'cases à cocher pdf',
      'formulaire pdf',
      'formulaires pdf remplissables',
      'constructeur de formulaires pdf'
    ],
    canonical: 'https://form-pdf.com/',
    ogImage: 'https://form-pdf.com/og-image.jpg',
    twitterCard: 'summary_large_image',
    lang: 'fr'
  },
  es: {
    title: 'Editor de Formularios PDF - Crea Formularios PDF Interactivos en Línea',
    description: 'Crea formularios PDF interactivos en línea con nuestro editor PDF gratuito. Agrega campos de texto, casillas de verificación y elementos de formulario a cualquier documento PDF. Rápido, seguro y basado en navegador.',
    keywords: [
      'editor pdf',
      'creador de formularios pdf',
      'pdf interactivo',
      'campos de formulario pdf',
      'editor pdf en línea',
      'campos de texto pdf',
      'casillas de verificación pdf',
      'formulario pdf',
      'formularios pdf rellenables',
      'constructor de formularios pdf'
    ],
    canonical: 'https://form-pdf.com/',
    ogImage: 'https://form-pdf.com/og-image.jpg',
    twitterCard: 'summary_large_image',
    lang: 'es'
  }
};

// Utility function to update meta tags
export function updateMetaTags(config: SEOConfig) {
  // Update title
  document.title = config.title;

  // Update or create meta tags
  const metaTags = [
    { name: 'description', content: config.description },
    { name: 'keywords', content: config.keywords.join(', ') },
    { name: 'language', content: config.lang || 'en' },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'PDF Form Editor' },
    { name: 'application-name', content: 'PDF Form Editor' },
    { name: 'theme-color', content: '#1971c2' },
    
    // Open Graph tags
    { property: 'og:title', content: config.title },
    { property: 'og:description', content: config.description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: config.canonical || window.location.href },
    { property: 'og:image', content: config.ogImage || '/og-image.jpg' },
    { property: 'og:image:alt', content: config.title },
    { property: 'og:site_name', content: 'PDF Form Editor' },
    { property: 'og:locale', content: config.lang === 'fr' ? 'fr_FR' : config.lang === 'es' ? 'es_ES' : 'en_US' },
    
    // Twitter Card tags
    { name: 'twitter:card', content: config.twitterCard || 'summary_large_image' },
    { name: 'twitter:title', content: config.title },
    { name: 'twitter:description', content: config.description },
    { name: 'twitter:image', content: config.ogImage || '/og-image.jpg' },
    { name: 'twitter:image:alt', content: config.title }
  ];

  // Update existing meta tags or create new ones
  metaTags.forEach(({ name, property, content }) => {
    const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
    let metaTag = document.querySelector(selector) as HTMLMetaElement;
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (name) metaTag.name = name;
      if (property) metaTag.setAttribute('property', property);
      document.head.appendChild(metaTag);
    }
    
    metaTag.content = content;
  });

  // Update canonical URL
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.href = config.canonical || window.location.href;

  // Update html lang attribute
  document.documentElement.lang = config.lang || 'en';

  // Add alternate language links
  // Remove existing alternate links
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => link.remove());
  
  if (config.alternates) {
    config.alternates.forEach(({ lang, url }) => {
      const alternateLink = document.createElement('link');
      alternateLink.rel = 'alternate';
      alternateLink.hreflang = lang;
      alternateLink.href = url;
      document.head.appendChild(alternateLink);
    });
  }
}

// React hook for SEO management
export function useSEO(config: SEOConfig | null, enabled: boolean = true) {
  useEffect(() => {
    if (!enabled || !config) return;
    
    updateMetaTags(config);
    
    // Cleanup function (optional - for SPA navigation)
    return () => {
      // Reset to default if needed
    };
  }, [config, enabled]);
}

// Language detection and management
export function detectLanguage(): 'en' | 'fr' | 'es' {
  // Check URL parameter first
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang') as 'en' | 'fr' | 'es';
  if (langParam && ['en', 'fr', 'es'].includes(langParam)) {
    return langParam;
  }

  // Check localStorage
  const storedLang = localStorage.getItem('preferred-language') as 'en' | 'fr' | 'es';
  if (storedLang && ['en', 'fr', 'es'].includes(storedLang)) {
    return storedLang;
  }

  // Check browser language
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('fr')) return 'fr';
  if (browserLang.startsWith('es')) return 'es';
  
  return 'en'; // default
}

// Save language preference
export function setLanguagePreference(lang: 'en' | 'fr' | 'es') {
  localStorage.setItem('preferred-language', lang);
}

// Generate structured data (JSON-LD)
export function generateStructuredData(lang: 'en' | 'fr' | 'es'): string {
  const seo = defaultSEO[lang];
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": seo.title,
    "description": seo.description,
    "url": seo.canonical,
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "author": {
      "@type": "Organization",
      "name": "PDF Form Editor"
    },
    "inLanguage": lang,
    "browserRequirements": "Requires JavaScript. Requires HTML5.",
    "permissions": "No installation required",
    "storageRequirements": "Minimal storage required for temporary files"
  };

  return JSON.stringify(structuredData, null, 2);
}

// Update structured data in DOM
export function updateStructuredData(lang: 'en' | 'fr' | 'es') {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = generateStructuredData(lang);
  document.head.appendChild(script);
}